/**
 * Enhanced Cognitive/Psychometric Report Generator
 * Generates detailed cognitive and psychometric reports
 * Now supports AI-generated templates with graceful fallback
 */

import { 
  fetchTraitTemplate, 
  fetchBlueprintTemplate,
  fetchSeatingTemplate,
  fetchRecommendationsTemplate,
  fetchCareerGuidanceTemplate,
  fetchNextStepsTemplate,
  personalizeTemplate
} from './templateService.js';

export async function generateEnhancedCognitiveReport(personalInfo, cognitiveProfile, responses) {
  if (!cognitiveProfile) return null;

  const age = personalInfo.age || 15;
  const sport = personalInfo.sport || 'Athletics';

  // Determine brain hemisphere dominance
  const hemisphereProfile = determineBrainHemisphere(cognitiveProfile, responses);
  
  // Generate cognitive performance scores
  const performanceScores = generatePerformanceScores(responses);
  
  // Generate trait analyses (now async with template support)
  const traitAnalyses = await generateTraitAnalyses(cognitiveProfile, responses, personalInfo);
  
  // Generate dominance profile
  const dominanceProfile = generateDominanceProfile(responses);

  return {
    // Header
    studentName: `${personalInfo.firstName} ${personalInfo.lastName}`,
    school: personalInfo.schoolName,
    grade: personalInfo.grade,
    section: personalInfo.section,
    age,
    sport,
    reportType: 'Cognitive Discernment Report',

    // Blueprint Badge
    blueprint: {
      type: hemisphereProfile.type,
      temperament: hemisphereProfile.temperament,
      description: hemisphereProfile.description
    },

    // Dominance Profile (Brain, Eye, Ear, Leg, Foot)
    dominanceProfile,

    // 5 Cognitive Performance Scores
    performanceScores,

    // Trait Analyses (expandable sections)
    traitAnalyses,

    // Ideal Seating Position
    idealSeating: {
      position: hemisphereProfile.dominance === 'left' ? 'Front-Left' : 'Front-Right',
      description: `${hemisphereProfile.dominance === 'left' ? 'Left' : 'Right'}-brain dominant individuals often prefer front-${hemisphereProfile.dominance} seating to optimize their ${hemisphereProfile.dominance === 'left' ? 'sequential processing and analytical focus' : 'creative thinking and holistic understanding'}.`,
      diagram: hemisphereProfile.dominance
    },

    // Recommendations
    recommendations: generateCognitiveRecommendations(cognitiveProfile, hemisphereProfile),

    // Learning Style
    learningStyle: cognitiveProfile.profile.learningStyle,

    // Career Guidance
    careerGuidance: generateCareerGuidance(cognitiveProfile, sport),

    // Next Steps
    nextSteps: generateNextSteps(hemisphereProfile, cognitiveProfile)
  };
}

function determineBrainHemisphere(cognitiveProfile, responses) {
  // Extract dominance data from responses
  const dominanceData = {
    eye: null,
    hand: null,
    foot: null
  };

  responses.forEach(response => {
    if (response.dominance) {
      const [bodyPart, side] = response.dominance.split('_');
      if (['eye', 'hand', 'foot'].includes(bodyPart)) {
        dominanceData[bodyPart] = side;
      }
    }
  });

  // Generate blueprint type (e.g., LLL, RRR, LRL, RLR, etc.)
  const eyeLetter = dominanceData.eye === 'left' ? 'L' : dominanceData.eye === 'right' ? 'R' : 'N';
  const handLetter = dominanceData.hand === 'left' ? 'L' : dominanceData.hand === 'right' ? 'R' : 'N';
  const footLetter = dominanceData.foot === 'left' ? 'L' : dominanceData.foot === 'right' ? 'R' : 'N';
  const type = `${eyeLetter}${handLetter}${footLetter}`;

  // Determine overall brain hemisphere dominance based on hand dominance
  const dominance = dominanceData.hand === 'left' ? 'left' : 'right';
  
  // Determine temperament based on cognitive traits
  const analyticalTraits = ['methodical', 'analytical', 'detail_oriented', 'structured'];
  const creativeTraits = ['experimental', 'big_picture', 'flexible', 'intuitive'];
  
  let analyticalScore = 0;
  let creativeScore = 0;
  
  responses.forEach(response => {
    if (analyticalTraits.includes(response.trait)) analyticalScore++;
    if (creativeTraits.includes(response.trait)) creativeScore++;
  });

  const temperament = analyticalScore > creativeScore ? 'Analytical' : 'Creative';

  return {
    dominance,
    type,
    temperament,
    description: `demonstrates ${dominance} hemisphere dominance with a ${type} blueprint profile, indicating ${temperament === 'Analytical' ? 'an analytical' : 'a creative'} temperament. This cognitive profile suggests strong ${dominance === 'left' ? 'logical reasoning and sequential processing' : 'intuitive thinking and holistic processing'} abilities, making them well-suited for ${temperament === 'Analytical' ? 'structured, analytical' : 'creative, innovative'} tasks.`
  };
}

function generatePerformanceScores(responses) {
  // Map questions to cognitive domains based on their traits and categories
  const domainMapping = {
    memory: ['repetition_learner', 'teaching_learner', 'visual_organizer', 'auditory_learner'],
    attention: ['focused', 'sustained_attention', 'short_attention', 'visual_fatigue', 'distracted'],
    visualSpatial: ['visual_learner', 'visual_empathy', 'visual_communicator', 'visual_seeking', 'visual_sensitive'],
    processingSpeed: ['action_oriented', 'pressure_performer', 'flexible', 'adaptive', 'quick_decision'],
    executiveFunction: ['methodical', 'analytical', 'structured', 'problem_solver', 'growth_mindset', 'persistent']
  };

  // Count responses for each domain
  const domainScores = {
    memory: [],
    attention: [],
    visualSpatial: [],
    processingSpeed: [],
    executiveFunction: []
  };

  responses.forEach(response => {
    const trait = response.trait;
    const score = response.score || 2;
    
    // Check which domain this trait belongs to
    Object.keys(domainMapping).forEach(domain => {
      if (domainMapping[domain].includes(trait)) {
        domainScores[domain].push(score);
      }
    });
  });

  // Calculate average score for each domain and convert to 0-100 scale
  const calculateDomainScore = (scores) => {
    if (scores.length === 0) return 70; // Default baseline
    const avg = scores.reduce((sum, s) => sum + s, 0) / scores.length;
    // Convert 1-3 scale to 0-100 (1=50, 2=75, 3=100)
    return Math.round(25 + (avg * 25));
  };

  return [
    {
      domain: 'Memory',
      score: calculateDomainScore(domainScores.memory),
      description: 'Ability to retain and recall information',
      questionsAnalyzed: domainScores.memory.length
    },
    {
      domain: 'Attention',
      score: calculateDomainScore(domainScores.attention),
      description: 'Sustained focus and concentration',
      questionsAnalyzed: domainScores.attention.length
    },
    {
      domain: 'Visual Spatial',
      score: calculateDomainScore(domainScores.visualSpatial),
      description: 'Understanding spatial relationships',
      questionsAnalyzed: domainScores.visualSpatial.length
    },
    {
      domain: 'Processing Speed',
      score: calculateDomainScore(domainScores.processingSpeed),
      description: 'Speed of mental processing',
      questionsAnalyzed: domainScores.processingSpeed.length
    },
    {
      domain: 'Executive Function',
      score: calculateDomainScore(domainScores.executiveFunction),
      description: 'Planning, decision-making, and problem-solving',
      questionsAnalyzed: domainScores.executiveFunction.length
    }
  ];
}

async function generateTraitAnalyses(cognitiveProfile, responses, personalInfo) {
  const analyses = [];

  // Extract dominance data
  const dominanceData = {
    eye: null,
    hand: null,
    ear: null,
    leg: null,
    foot: null
  };

  responses.forEach(response => {
    if (response.dominance) {
      const [bodyPart, side] = response.dominance.split('_');
      dominanceData[bodyPart] = side;
    }
  });

  // Determine brain dominance based on hand dominance
  let brainDominance = 'right'; // Default
  if (dominanceData.hand === 'left') {
    brainDominance = 'left';
  } else if (dominanceData.hand === 'right') {
    brainDominance = 'right';
  }

  // Brain Hemisphere Dominance - Try template first, fallback to hardcoded
  const brainTemplate = await fetchTraitTemplate('brain', brainDominance);
  
  const brainDesc = brainTemplate 
    ? personalizeTemplate(brainTemplate.description, personalInfo)
    : (brainDominance === 'left'
      ? 'Your left hemisphere dominance reveals a mind built for precision, logic, and systematic thinking. You excel at breaking down complex problems into manageable steps, analyzing details, and following structured approaches. This analytical strength makes you naturally suited for tasks requiring sequential processing, mathematical reasoning, and methodical problem-solving.'
      : 'Your right hemisphere dominance reveals a mind built for creativity, intuition, and holistic thinking. You naturally see the big picture, make intuitive connections, and excel at creative problem-solving. This innovative strength makes you well-suited for tasks requiring spatial awareness, pattern recognition, and imaginative approaches to challenges.');

  const brainTraits = brainTemplate?.traits || (brainDominance === 'left'
    ? [
        'Strong logical reasoning and analytical thinking',
        'Excellent at sequential processing and step-by-step problem solving',
        'Detail-oriented with high attention to accuracy',
        'Systematic approach to learning and work',
        'Strong verbal and language processing skills'
      ]
    : [
        'Creative and intuitive thinking with innovative solutions',
        'Excellent at seeing patterns and big-picture connections',
        'Strong spatial awareness and holistic understanding',
        'Visual-spatial processing and artistic abilities',
        'Ability to synthesize information from multiple sources'
      ]);

  analyses.push({
    title: 'Brain Hemisphere Dominance',
    icon: '🧠',
    description: brainDesc,
    traits: brainTraits,
    preferences: [
      'Structured learning with clear objectives',
      'Step-by-step instructions',
      'Logical explanations and reasoning',
      'Organized study materials'
    ],
    stressors: [
      'Ambiguous or unclear instructions',
      'Lack of structure or organization',
      'Too much creative freedom without guidelines',
      'Rushed decision-making'
    ],
    hintsAndTips: [
      'Create detailed study schedules and stick to them',
      'Break large projects into smaller, manageable tasks',
      'Use lists, charts, and organized notes',
      'Practice creative thinking through art or music to develop balance'
    ],
    overview: 'Your left brain dominance (94 vs 6) influences how you process information sequentially and analytically. This makes you excellent at tasks requiring logic, mathematics, and systematic problem-solving.'
  });

  // Eye Dominance & Visual Processing
  const eyeDominance = dominanceData.eye || 'right';
  const eyeDesc = `Your ${eyeDominance} eye dominance indicates strong visual-spatial processing and attention to visual details. You learn best through seeing and observing.`;

  analyses.push({
    title: 'Eye Dominance & Visual Processing',
    icon: '👁️',
    description: eyeDesc,
    traits: [
      'Strong visual memory and recall',
      'Excellent at reading and interpreting visual information',
      'Detail-oriented visual processing',
      'Prefers visual learning materials (diagrams, charts, videos)'
    ],
    preferences: [
      'Visual aids and diagrams',
      'Color-coded notes and materials',
      'Watching demonstrations',
      'Mind maps and flowcharts'
    ],
    stressors: [
      'Purely auditory instruction without visuals',
      'Poor lighting or visual distractions',
      'Lack of visual organization'
    ],
    hintsAndTips: [
      'Use visual study aids like diagrams and mind maps',
      'Color-code your notes for better retention',
      'Watch video demonstrations before attempting new skills',
      'Create visual summaries of complex topics'
    ],
    overview: 'Your left eye dominance (8 vs 0) affects how you process visual information and prefer to receive verbal communication. Understanding this helps you leverage your visual strengths and develop complementary skills.'
  });

  // Ear Dominance & Auditory Processing
  const earDominance = dominanceData.ear || 'right';
  const earDesc = `Your ${earDominance} ear dominance influences how you process auditory information and prefer to receive verbal communication.`;

  analyses.push({
    title: 'Ear Dominance & Auditory Processing',
    icon: '👂',
    description: earDesc,
    traits: [
      'Prefers verbal explanations',
      'Processes auditory information effectively',
      'Good at following spoken instructions'
    ],
    preferences: [
      'Verbal explanations and discussions',
      'Audio recordings of lectures',
      'Group discussions and debates',
      'Reading aloud or explaining concepts verbally'
    ],
    stressors: [
      'Noisy environments during learning',
      'Lack of verbal clarification',
      'Silent study without discussion'
    ],
    hintsAndTips: [
      'Record lectures and listen to them multiple times',
      'Participate in study groups and discussions',
      'Explain concepts out loud to yourself or others',
      'Use podcasts and audiobooks for learning'
    ],
    overview: 'Your left ear dominance (12 vs 0) influences how you process auditory information and prefer to receive verbal communication.'
  });

  // Hand Dominance & Motor Skills
  const handDominance = dominanceData.hand || 'right';
  const handDesc = handDominance === 'left'
    ? 'Your left hand dominance is associated with right brain control, often linked to creative and artistic abilities.'
    : 'Your right hand dominance is associated with left brain control, often linked to analytical and logical abilities.';
  
  const handTraits = handDominance === 'left'
    ? [
        'Creative and artistic motor skills',
        'Unique problem-solving approaches',
        'Adaptable in physical tasks',
        'Often excel in arts and creative fields'
      ]
    : [
        'Precise and controlled motor skills',
        'Systematic approach to physical tasks',
        'Strong hand-eye coordination',
        'Excel in tasks requiring precision'
      ];

  analyses.push({
    title: 'Hand Dominance & Motor Skills',
    icon: '✋',
    description: handDesc,
    traits: handTraits,
    preferences: [
      'Hands-on learning',
      'Physical practice and repetition',
      'Creative expression through movement',
      'Kinesthetic learning activities'
    ],
    hintsAndTips: [
      'Practice fine motor skills through writing or drawing',
      'Engage in activities that use both hands',
      'Develop ambidexterity for cognitive flexibility',
      'Use your dominant hand for complex tasks'
    ],
    overview: 'Your left hand dominance (6 vs 0) reflects your motor control preferences and can influence your approach to physical and creative tasks.'
  });

  // Foot Dominance & Balance
  const footDominance = dominanceData.foot || 'right';
  const footDesc = `Your ${footDominance} foot dominance affects balance, coordination, and athletic performance.`;

  analyses.push({
    title: 'Foot Dominance & Balance',
    icon: '🦶',
    description: footDesc,
    traits: [
      'Strong balance and coordination',
      'Athletic agility',
      'Spatial awareness'
    ],
    preferences: [
      'Physical activities and sports',
      'Movement-based learning',
      'Active breaks during study'
    ],
    hintsAndTips: [
      'Incorporate movement into your learning routine',
      'Practice balance exercises for both feet',
      'Use physical activity to enhance focus',
      'Develop bilateral coordination'
    ],
    overview: 'Your foot dominance influences your balance, coordination, and athletic performance.'
  });

  return analyses;
}

function generateDominanceProfile(responses) {
  // Extract dominance from responses
  const dominanceData = {
    eye: null,
    hand: null,
    ear: null,
    leg: null,
    foot: null
  };

  responses.forEach(response => {
    if (response.dominance) {
      const [bodyPart, side] = response.dominance.split('_');
      dominanceData[bodyPart] = side;
    }
  });

  // Determine brain dominance based on hand dominance (standard neurological correlation)
  let brainDominance = 'right'; // Default
  if (dominanceData.hand === 'left') {
    brainDominance = 'left';
  } else if (dominanceData.hand === 'right') {
    brainDominance = 'right';
  }

  return {
    brain: { 
      left: brainDominance === 'left', 
      right: brainDominance === 'right' 
    },
    eye: { 
      left: dominanceData.eye === 'left', 
      right: dominanceData.eye === 'right' 
    },
    ear: { 
      left: dominanceData.ear === 'left', 
      right: dominanceData.ear === 'right' 
    },
    hand: { 
      left: dominanceData.hand === 'left', 
      right: dominanceData.hand === 'right' 
    },
    leg: { 
      left: dominanceData.leg === 'left', 
      right: dominanceData.leg === 'right' 
    },
    foot: { 
      left: dominanceData.foot === 'left', 
      right: dominanceData.foot === 'right' 
    }
  };
}

function generateCognitiveRecommendations(cognitiveProfile, hemisphereProfile) {
  const recommendations = [];

  if (hemisphereProfile.dominance === 'left') {
    recommendations.push('Leverage your left-brain strengths in analytical tasks');
    recommendations.push('Practice creative thinking exercises to develop balance');
    recommendations.push('Use visual learning methods for optimal retention');
    recommendations.push('Consider careers in STEM fields that align with your strengths');
  } else {
    recommendations.push('Leverage your right-brain strengths in creative tasks');
    recommendations.push('Practice analytical thinking to develop balance');
    recommendations.push('Use hands-on learning methods for optimal retention');
    recommendations.push('Consider careers in arts, design, or innovation fields');
  }

  return recommendations;
}

function generateCareerGuidance(cognitiveProfile, sport) {
  const guidance = [];
  const socialStyle = cognitiveProfile.profile.socialStyle.type;
  const learningStyle = cognitiveProfile.profile.learningStyle.type;
  const leadershipStyle = cognitiveProfile.profile.leadershipStyle.type;

  // Social Style Guidance
  if (socialStyle === 'Extravert') {
    guidance.push(`As an extravert, you thrive in team environments where collaboration and social interaction are central. Your energy comes from engaging with others, making you naturally suited for team sports like ${sport}. You excel in group training sessions, team meetings, and collaborative strategy discussions. Consider pursuing leadership roles within your team, such as team captain or peer mentor, where you can leverage your social strengths to motivate and unite teammates. In your academic and career path, look for opportunities that involve public speaking, team coordination, and interpersonal communication. Fields like sports management, coaching, physical education, or sports psychology could align well with your extraverted nature and athletic interests.`);
  } else if (socialStyle === 'Introvert') {
    guidance.push(`As an introvert, you excel in environments that allow for deep focus, individual practice, and one-on-one interactions. While ${sport} may involve team dynamics, you'll perform best when you have time for solo skill development and personal reflection on your performance. Seek out coaches who provide individualized feedback and create quiet spaces for concentrated practice. Your introspective nature makes you excellent at self-analysis and technique refinement. In your career path, consider roles that leverage your analytical strengths, such as sports analytics, biomechanics research, sports technology development, or specialized coaching where you can work closely with individual athletes. Your ability to focus deeply will be a significant advantage in technical and research-oriented fields.`);
  } else {
    guidance.push(`As an ambivert, you have the flexibility to thrive in both team and individual settings. This balanced social style makes you highly adaptable in ${sport}, able to contribute effectively in group training while also excelling during solo practice sessions. You can switch between leading team discussions and working independently on skill development. This versatility is valuable in both athletic and career contexts. Consider roles that offer variety—perhaps combining coaching (social) with performance analysis (individual), or positions in sports organizations where you interact with teams but also have time for focused project work. Your balanced nature allows you to bridge different personality types and work styles effectively.`);
  }

  // Learning & Sport-Specific Guidance
  if (learningStyle === 'Hands-On Learner') {
    guidance.push(`Your hands-on learning style means you master skills through active practice and repetition. In ${sport}, focus on drill-based training where you can physically practice techniques repeatedly until they become second nature. Video analysis combined with immediate practice will accelerate your learning. Seek coaches who demonstrate techniques and then give you ample time to practice with feedback. For career development, consider paths that involve practical application: strength and conditioning coaching, sports equipment testing, physical therapy, or athletic training where you can work directly with athletes and equipment. Internships and hands-on experiences will be more valuable to you than purely theoretical coursework.`);
  } else if (learningStyle === 'Visual Learner') {
    guidance.push(`As a visual learner, you absorb information best through observation and visual aids. In ${sport}, maximize your learning by watching game footage, studying technique videos, and using visual playbooks or strategy diagrams. Create visual goal boards and track your progress with charts and graphs. Work with coaches who use demonstrations and visual feedback tools. For your career, consider fields that leverage visual analysis: sports video analysis, tactical coaching, sports journalism/broadcasting, or sports photography/videography. Your ability to see patterns and spatial relationships makes you well-suited for strategic planning roles in sports organizations or positions involving game analysis and opponent scouting.`);
  } else {
    guidance.push(`Your step-by-step learning approach means you excel when information is presented in a clear, sequential manner. In ${sport}, you'll benefit from structured training programs with progressive skill development. Break down complex techniques into smaller components and master each before moving forward. Seek coaches who provide detailed explanations and systematic training plans. For career development, your methodical approach suits roles in sports program development, training curriculum design, sports education, or administrative positions in athletic organizations. Your ability to create and follow structured systems makes you valuable in roles requiring organization, planning, and systematic implementation of sports programs.`);
  }

  // Leadership & Team Role Guidance
  if (leadershipStyle === 'Natural Leader') {
    guidance.push(`Your natural leadership abilities position you well for captain or leadership roles in ${sport}. You have the confidence and organizational skills to guide teammates, make strategic decisions under pressure, and inspire others through your actions. Beyond athletics, consider career paths in sports management, athletic administration, coaching, or sports entrepreneurship. Your leadership qualities will serve you well in any role where you need to motivate teams, manage programs, or drive organizational success. Develop your leadership skills further through captaincy opportunities, mentoring younger athletes, and taking on organizational responsibilities within your team or school sports programs.`);
  } else if (leadershipStyle === 'Supportive Team Player') {
    guidance.push(`Your supportive nature makes you an invaluable team member in ${sport}. You excel at creating positive team culture, supporting teammates through challenges, and ensuring everyone feels included and valued. While you may not seek the spotlight, your emotional intelligence and collaborative spirit are crucial for team success. Career paths that align with your supportive style include sports psychology, athletic counseling, team coordination, sports social work, or roles in athlete welfare and support services. You could also excel in coaching positions focused on youth development or working with athletes who need additional support and encouragement.`);
  } else {
    guidance.push(`Your collaborative approach means you work best when following clear guidance and contributing within established structures. In ${sport}, you'll thrive under coaches who provide clear direction and defined roles. Focus on mastering your specific position and responsibilities while supporting team objectives. For career development, consider roles that offer clear frameworks and team collaboration: assistant coaching positions, sports program coordination, athletic training support, or administrative roles in sports organizations. Your reliability and ability to execute plans make you valuable in operational and support roles that keep sports programs running smoothly.`);
  }

  return guidance;
}

function generateNextSteps(hemisphereProfile, cognitiveProfile) {
  return `Review this comprehensive cognitive profile to understand your natural strengths, learning preferences, and optimal environments. Share these insights with your coach to create a personalized development plan that leverages your cognitive strengths and addresses areas for growth. Remember, while you have specific ${hemisphereProfile.dominance} hemisphere dominance preferences, daily tasks will also use and access other non-dominant areas of your brain. Developing both hemispheres through varied activities will enhance your overall cognitive flexibility and performance.`;
}
